import { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMusic, faCompactDisc } from '@fortawesome/free-solid-svg-icons';
import { Header, Footer, VideoCard, VideoCardSkeleton } from './shared';
import { useYouTubeVideos } from '../hooks/useFetch';
import { YOUTUBE_CONFIG } from '../config/constants';
import { useDevice } from '../hooks/useMediaQuery';
import type { YouTubeVideo } from '../types';

const Music: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVideo, setSelectedVideo] = useState<YouTubeVideo | null>(null);
  useDevice(); // For future responsive enhancements

  const { videos, loading, error } = useYouTubeVideos(YOUTUBE_CONFIG.musicChannelId);

  const filteredVideos = useMemo(() => {
    if (!searchTerm.trim()) return videos;
    return videos.filter((video: YouTubeVideo) =>
      video.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [videos, searchTerm]);

  const handlePlayVideo = (video: YouTubeVideo) => {
    setSelectedVideo(video);
    // Scroll to player
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-dark-950">
      <Header
        onSearch={setSearchTerm}
        searchValue={searchTerm}
        showSearch={true}
      />

      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 lg:py-20 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 via-dark-950 to-pink-600/20" />
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 30% 30%, rgba(168, 85, 247, 0.2) 0%, transparent 50%),
                             radial-gradient(circle at 70% 70%, rgba(236, 72, 153, 0.2) 0%, transparent 50%)`,
          }} />
          {/* Music notes animation */}
          <div className="absolute inset-0 overflow-hidden opacity-10">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute text-white animate-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  fontSize: `${Math.random() * 20 + 10}px`,
                }}
              >
                ♪
              </div>
            ))}
          </div>
        </div>

        <div className="relative max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full
              bg-purple-500/20 border border-purple-500/30 mb-6 animate-fade-in">
              <FontAwesomeIcon icon={faCompactDisc} className="text-purple-400 animate-spin"
                style={{ animationDuration: '3s' }} />
              <span className="text-purple-300 font-medium">Black Alpha Productions</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black mb-4
              animate-fade-in" style={{ animationDelay: '100ms' }}>
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-rose-400 
                bg-clip-text text-transparent">
                Music
              </span>
            </h1>
            <p className="text-gray-400 text-lg sm:text-xl lg:text-2xl max-w-2xl mx-auto 
              animate-fade-in" style={{ animationDelay: '200ms' }}>
              Discover amazing tracks and music videos
            </p>
          </div>

          {/* Stats */}
          <div className="flex justify-center gap-8 sm:gap-12 lg:gap-16 
            text-center animate-fade-in" style={{ animationDelay: '300ms' }}>
            <div>
              <p className="text-3xl sm:text-4xl lg:text-5xl font-bold 
                bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                {videos.length}
              </p>
              <p className="text-gray-500 text-sm sm:text-base">Videos</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl lg:text-5xl font-bold 
                bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                HD
              </p>
              <p className="text-gray-500 text-sm sm:text-base">Quality</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl lg:text-5xl font-bold 
                bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                ∞
              </p>
              <p className="text-gray-500 text-sm sm:text-base">Free</p>
            </div>
          </div>
        </div>
      </section>

      {/* Selected Video Player */}
      {selectedVideo && (
        <section className="max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 
          mb-12 animate-fade-in">
          <div className="relative rounded-2xl overflow-hidden 
            bg-dark-900 shadow-2xl shadow-purple-500/10">
            <div className="aspect-video">
              <iframe
                src={`https://www.youtube.com/embed/${selectedVideo.id}?autoplay=1`}
                title={selectedVideo.title}
                className="w-full h-full"
                allowFullScreen
                allow="autoplay; encrypted-media; fullscreen"
              />
            </div>
            <div className="p-4 sm:p-6 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold mb-2">{selectedVideo.title}</h2>
                <p className="text-gray-400">Now Playing</p>
              </div>
              <button
                onClick={() => setSelectedVideo(null)}
                className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20
                  transition-colors text-sm font-medium"
              >
                Close Player
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Content */}
      <main className="max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 pb-20">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold">
            {searchTerm ? (
              <>
                Results for <span className="text-purple-400">"{searchTerm}"</span>
              </>
            ) : (
              <>All <span className="text-gradient">Music Videos</span></>
            )}
          </h2>
          {filteredVideos.length > 0 && (
            <span className="text-gray-500">
              {filteredVideos.length} video{filteredVideos.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="video-grid">
            <VideoCardSkeleton count={12} />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-20">
            <p className="text-red-400 text-xl mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-purple-500 rounded-full font-semibold
                hover:bg-purple-600 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Videos Grid */}
        {!loading && !error && (
          <>
            {filteredVideos.length > 0 ? (
              <div className="video-grid">
                {filteredVideos.map((video, index) => (
                  <VideoCard
                    key={video.id}
                    video={video}
                    index={index}
                    type="music"
                    onPlay={handlePlayVideo}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <FontAwesomeIcon icon={faMusic} className="text-6xl text-gray-700 mb-4" />
                <p className="text-gray-400 text-xl mb-4">
                  {searchTerm
                    ? `No music found for "${searchTerm}"`
                    : 'No music videos available'
                  }
                </p>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="text-purple-400 hover:text-purple-300 font-semibold"
                  >
                    Clear search
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Music;
